import { resolveCompanyId } from '../../../utils/companyIdentity';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Container, Typography, Paper, Grid, Chip, Button, CircularProgress } from '@mui/material';
import { Assessment, Event, Groups, Handshake, Refresh, TrendingUp } from '@mui/icons-material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useAuth } from '../../auth/AuthContext';
import Header from '../../common/Header/Header';
import LoadingSpinner from '../../common/LoadingSpinner';

const Stat = ({ label, value, icon, note }) => (
    <Paper className="erp-glass-card erp-glass-card--interactive erp-shine" elevation={0} sx={{ p: 1.5, height: 'auto', minHeight: 118 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box>
                <Typography sx={{ fontSize: '.7rem', fontWeight: 800, letterSpacing: '.08em', color: 'var(--text-secondary)' }}>{label}</Typography>
                <Typography sx={{ mt: .45, fontSize: '1.85rem', lineHeight: 1, fontWeight: 850, color: 'var(--text-primary)' }}>{value}</Typography>
                <Typography sx={{ mt: .75, fontSize: '.72rem', color: 'var(--text-secondary)' }}>{note}</Typography>
            </Box>
            <Box sx={{ width: 44, height: 44, borderRadius: '13px', display: 'grid', placeItems: 'center', color: 'var(--brand-action-text,#fff)', background: 'linear-gradient(145deg, var(--primary), var(--secondary))', boxShadow: '0 10px 24px rgba(var(--primary-rgb),.22)' }}>
                {icon}
            </Box>
        </Box>
    </Paper>
);

const CompanyAnalytics = () => {
    const { companyData, userData, currentUser } = useAuth();
    const companyId = resolveCompanyId({ companyData, userData, currentUser });
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ meetings: 0, completed: 0, team: 0, connections: 0 });

    const load = useCallback(async () => {
        if (!companyId) { setLoading(false); return; }
        setLoading(true);
        try {
            const meetingsRef = collection(db, 'meetings');
            const [sent, received, legacySent, legacyReceived, team, sentInvites, receivedInvites] = await Promise.all([
                getDocs(query(meetingsRef, where('requesterId', '==', companyId))),
                getDocs(query(meetingsRef, where('targetId', '==', companyId))),
                getDocs(query(meetingsRef, where('companyId', '==', companyId))),
                getDocs(query(meetingsRef, where('targetCompanyId', '==', companyId))),
                getDocs(query(collection(db, 'users'), where('companyId', '==', companyId))),
                getDocs(query(collection(db, 'invitations'), where('companyId', '==', companyId))),
                getDocs(query(collection(db, 'invitations'), where('targetCompanyId', '==', companyId))),
            ]);
            const meetings = new Map();
            [...sent.docs, ...received.docs, ...legacySent.docs, ...legacyReceived.docs].forEach(d => meetings.set(d.id, d.data()));
            const invites = new Map();
            [...sentInvites.docs, ...receivedInvites.docs].forEach(d => invites.set(d.id, d.data()));
            setStats({
                meetings: meetings.size,
                completed: [...meetings.values()].filter(m => m.status === 'completed').length,
                team: team.docs.filter(d => !d.data().isBlocked).length,
                connections: [...invites.values()].filter(i => i.status === 'accepted' || i.status === 'connected').length,
            });
        } catch (error) {
            console.error('Company analytics load failed:', error);
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => { load(); }, [load]);
    const completion = useMemo(() => stats.meetings ? Math.round((stats.completed / stats.meetings) * 100) : 0, [stats]);

    if (loading && stats.meetings === 0 && stats.team === 0 && stats.connections === 0) {
        return <LoadingSpinner label="Loading company analytics…" />;
    }

    return (
        <Box className="erp-page-shell erp-internal-page tf-analytics-page" sx={{ minHeight: '100vh', background: 'transparent' }}>
            <Header />
            <Container maxWidth="xl" sx={{ py: { xs: 1.75, md: 2.25 } }}>
                <Paper className="erp-page-hero" elevation={0} sx={{ p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 48, height: 48, borderRadius: '14px', display: 'grid', placeItems: 'center', color: 'var(--brand-action-text,#fff)', background: 'linear-gradient(145deg,var(--primary),var(--secondary))' }}><Assessment /></Box>
                            <Box>
                                <Typography className="erp-page-title">Company Analytics</Typography>
                                <Typography className="erp-page-subtitle">Live operational snapshot for {companyData?.companyName || userData?.companyName || 'your company'}.</Typography>
                            </Box>
                        </Box>
                        <Button onClick={load} startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Refresh />} disabled={loading} variant="outlined" sx={{ borderColor: 'rgba(var(--primary-rgb),.24)', color: 'var(--primary)' }}>Refresh</Button>
                    </Box>
                </Paper>

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}><Stat label="TOTAL MEETINGS" value={stats.meetings} icon={<Event />} note="Sent and received meetings" /></Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}><Stat label="COMPLETED" value={stats.completed} icon={<TrendingUp />} note={`${completion}% completion rate`} /></Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}><Stat label="TEAM MEMBERS" value={stats.team} icon={<Groups />} note="Active, non-blocked users" /></Grid>
                    <Grid size={{ xs: 12, sm: 6, lg: 3 }}><Stat label="CONNECTIONS" value={stats.connections} icon={<Handshake />} note="Accepted company connections" /></Grid>
                </Grid>

                <Paper className="erp-glass-card" elevation={0} sx={{ p: 1.6, mt: 1.4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip size="small" label="Live data" sx={{ bgcolor: 'rgba(var(--primary-rgb),.09)', color: 'var(--primary)' }} />
                        <Typography sx={{ fontWeight: 800 }}>Performance summary</Typography>
                    </Box>
                    <Typography sx={{ color: 'var(--text-secondary)', fontSize: '.82rem', lineHeight: 1.7 }}>
                        This page now reads the same meetings, users and invitations collections used by the operating screens, so the analytics route no longer points to an empty component.
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};

export default CompanyAnalytics;
